/**
 * Auth context — Cognito Hosted UI flow.
 *
 * What changed from the legacy implementation:
 *   - No more `login(email, password)`. Use `loginWithCognito()` to redirect
 *     to the Hosted UI.
 *   - Access/ID tokens are kept in memory only. The long-lived refresh token
 *     lives in an httpOnly cookie set by the backend, so it can't be touched
 *     from JS (XSS-resistant).
 *   - On hard reload we attempt `/api/auth/refresh` to get a fresh access
 *     token from the cookie. If that fails, the user is unauthenticated.
 *
 * What stayed the same:
 *   - `useAuth().user` exposes the same shape (id, email, name, role,
 *     fund_ids, …) so every consumer (ProtectedRoute, sidebars, role-aware
 *     pages) keeps working untouched.
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import API, { setAccessToken, clearClientSession, setLoginInProgress } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  const scheduleRefresh = useCallback((expiresInSeconds) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    // Refresh 60s before expiry, never less than 30s out.
    const delayMs = Math.max(30, (expiresInSeconds || 3600) - 60) * 1000;
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await API.post("/auth/refresh");
        setAccessToken(data.access_token);
        scheduleRefresh(data.expires_in);
      } catch {
        setAccessToken(null);
        setUser(null);
      }
    }, delayMs);
  }, []);

  const hydrateFromCookie = useCallback(async () => {
    try {
      const { data } = await API.post("/auth/refresh");
      setAccessToken(data.access_token);
      scheduleRefresh(data.expires_in);
      const me = await API.get("/auth/me");
      setUser(me.data);
    } catch {
      setAccessToken(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [scheduleRefresh]);

  useEffect(() => {
    // Skip silent rehydrate on /callback — that page is in the middle of the
    // OAuth code-exchange and will call `completeLogin` itself. Firing
    // `/auth/refresh` here races the exchange and wipes the freshly-set user.
    if (typeof window !== "undefined" && window.location.pathname === "/callback") {
      setIsLoading(false);
      return () => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      };
    }
    hydrateFromCookie();
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [hydrateFromCookie]);

  // ── Cross-app SLO detector ─────────────────────────────────────────
  // Open tabs: on window focus, on route change, and every 20 s poll,
  // probe /api/auth/me. If it returns 401 (or the network hop 401s),
  // the server-side session has been revoked (either from another tab
  // or from a sibling app's Master SLO fan-out) — hard-redirect to
  // Master's own /login page. Deliberately NO auto-SSO: we want the
  // operator to explicitly re-authenticate.
  const location = useLocation();
  const invalidatedRef = useRef(false);
  const probeSession = useCallback(async () => {
    if (invalidatedRef.current) return;
    if (!user) return; // Nothing to invalidate; hydrate path handles boot.
    try {
      await API.get("/auth/me");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        invalidatedRef.current = true;
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        clearClientSession({ preserveDeepLink: false });
        setAccessToken(null);
        setUser(null);
        // Hard redirect — replace so the invalidated page doesn't come
        // back on Back button.
        if (typeof window !== "undefined") window.location.replace("/login");
      }
    }
  }, [user]);
  // Focus + interval poll. Interval sits at 20 s (inside the 15–30 s
  // range specified in the ticket).
  useEffect(() => {
    if (!user) return undefined;
    const onFocus = () => probeSession();
    const onVisibility = () => { if (document.visibilityState === "visible") probeSession(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    const t = setInterval(probeSession, 20 * 1000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(t);
    };
  }, [user, probeSession]);
  // Route-change probe.
  useEffect(() => {
    if (!user) return;
    probeSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Redirect to Cognito Hosted UI for sign-in. Returns a Promise that never
  // resolves in this tab — the browser navigates away.
  const loginWithCognito = async () => {
    // Pass the caller's own origin so Cognito comes back to whatever host
    // the user is actually on (app.tychi.co in production, preview URL in
    // dev). This makes the backend independent of `COGNITO_REDIRECT_URI`
    // env-var drift after a custom-domain switchover.
    const redirectUri = `${window.location.origin}/callback`;
    const { data } = await API.get("/auth/login-url", { params: { redirect_uri: redirectUri } });
    window.location.href = data.url;
  };

  // Called by /callback page after exchanging the code for tokens.
  const completeLogin = (tokens) => {
    // Signal the 401 interceptor that a login flow is active — it must
    // NOT call clearClientSession while we are storing the fresh token.
    setLoginInProgress(true);
    try {
      // If a DIFFERENT user is taking over this browser (context change),
      // forcefully purge any stale auth/user state first so the previous
      // user's token / role / fund_ids / org_id can never leak through.
      // preserveDeepLink keeps the one-shot SSO keys the /callback page is
      // about to read right after this call.
      const prevId = localStorage.getItem("tychi_last_user_id");
      const newId = tokens?.user?.id || null;
      if (prevId && newId && prevId !== newId) {
        clearClientSession({ preserveDeepLink: true });
      }
      // Store the new token AFTER any cleanup — never before.
      setAccessToken(tokens.access_token);
      setUser(tokens.user);
      if (newId) {
        try { localStorage.setItem("tychi_last_user_id", newId); } catch { /* ignore */ }
      }
      scheduleRefresh(tokens.expires_in);
    } finally {
      setLoginInProgress(false);
    }
  };

  const logout = async () => {
    // TASK: unified logout. Master session teardown is authoritative for
    // THIS browser — we always clear local state and redirect to Cognito
    // Hosted UI logout even when downstream SLO fan-out (GL/Radar/Recon)
    // is degraded. The backend returns `master_ok:true` whenever the
    // cookie was cleared + refresh revoked.
    let masterOk = false;
    let overallOk = false;
    let servicesReport = null;
    let correlationId = "";
    try {
      const { data } = await API.post("/auth/logout");
      masterOk = data?.master_ok !== false;
      overallOk = !!data?.overall_ok;
      servicesReport = data?.services || null;
      correlationId = data?.correlation_id || "";
    } catch (err) {
      // Endpoint failure — still tear down local state below. Master
      // cookie is cleared server-side unless the request itself never
      // arrived; either way we treat the browser session as ended.
      // eslint-disable-next-line no-console
      console.warn("[logout] backend call failed:", err?.message || err);
    }
    // Retry ONCE only if backend hinted a transient downstream failure.
    if (masterOk && !overallOk && servicesReport) {
      const transient = Object.values(servicesReport).some(
        (s) => s && s.needs_retry && !s.service_unavailable,
      );
      if (transient) {
        try {
          const { data } = await API.post("/auth/logout");
          overallOk = !!data?.overall_ok;
          servicesReport = data?.services || servicesReport;
        } catch { /* ignore */ }
      }
    }
    if (!overallOk) {
      // eslint-disable-next-line no-console
      console.warn(
        "[logout] partial SLO — forcing local logout. cid=",
        correlationId, "services=", servicesReport,
      );
    }
    // Complete client-side cleanup BEFORE redirecting: wipe the
    // in-memory tokens, localStorage, sessionStorage, JS-readable cookies
    // and all cached user/session state (role, permissions, fund_ids,
    // org_id, user_id, …).
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    clearClientSession({ preserveDeepLink: false });
    setUser(null);
    try {
      // Same trick as login: pass the caller's origin so Cognito comes back
      // to the right host post-logout.
      const redirectUri = `${window.location.origin}/login`;
      const { data } = await API.get("/auth/logout-url", { params: { redirect_uri: redirectUri } });
      window.location.href = data.url;
    } catch {
      window.location.href = "/login";
    }
  };

  const updateUser = (updates) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        loginWithCognito,
        completeLogin,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
